import React, { useState, useCallback } from 'react';
import { Select, Space, Card, InputNumber, Typography } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { type Locale, LOCALE_OPTIONS, SUPPORTED_LOCALES, DEFAULT_LOCALE } from './types';
import { formatCreditLimit } from './locales';
import { Currency } from '../../features/master-data/types/customer.types';

const { Title, Text } = Typography;

/**
 * Hook to use locale in components
 * Uses react-i18next for language management
 */
function useLocaleInternal() {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<Locale>(() => {
    const stored = localStorage.getItem('erp-locale');
    return (SUPPORTED_LOCALES.includes(stored as Locale) ? stored : DEFAULT_LOCALE) as Locale;
  });

  const changeLocale = useCallback(async (newLocale: Locale) => {
    await i18n.changeLanguage(newLocale);
    setLocaleState(newLocale);
    localStorage.setItem('erp-locale', newLocale);
  }, [i18n]);

  return {
    locale,
    setLocale: changeLocale,
  };
}

/**
 * Locale Switcher Component
 *
 * Allows users to switch between supported locales.
 * Uses react-i18next for language management.
 *
 * Selector Contract:
 * - data-testid="locale-switcher" for E2E tests
 * - aria-label="Select language" for accessibility
 */
interface LocaleSwitcherProps {
  /** Compact header mode for narrow mobile viewports */
  compact?: boolean;
}

export const LocaleSwitcher: React.FC<LocaleSwitcherProps> = ({ compact = false }) => {
  const { locale, setLocale } = useLocaleInternal();
  const { t } = useTranslation('common');

  const handleChange = async (value: Locale) => {
    await setLocale(value);
  };

  return (
    <div data-testid="locale-switcher">
      <Select
        value={locale}
        onChange={handleChange}
        style={compact ? { width: 64 } : { minWidth: 140 }}
        suffixIcon={<GlobalOutlined />}
        optionLabelProp={compact ? 'title' : 'label'}
        popupMatchSelectWidth={false}
        options={LOCALE_OPTIONS.map((opt) => ({
          value: opt.value,
          title: opt.flag,
          label: (
            <Space>
              <span>{opt.flag}</span>
              <span>{opt.label}</span>
            </Space>
          ),
        }))}
        aria-label={t('aria.selectLanguage')}
      />
    </div>
  );
};

/**
 * Locale Switcher Demo Component
 *
 * Demonstrates the i18n system for locale-specific number/currency formatting
 * This is a demo component that can be used for testing i18n features
 */
export const LocaleSwitcherDemo: React.FC = () => {
  const { t, i18n } = useTranslation('common');
  const { locale } = useLocaleInternal();
  const [value, setValue] = useState(150000);

  const handleLocaleChange = async (newLocale: Locale) => {
    await i18n.changeLanguage(newLocale);
  };

  const currency: Currency = Currency.CNY;
  const unit = 'TEN_THOUSAND';

  return (
    <Card style={{ maxWidth: 600, margin: '24px auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={4}>🌏 {t('localeDemo.title')}</Title>
          <Text type="secondary">
            Demo: Internationalization (i18n) for credit limit formatting
          </Text>
        </div>

        {/* Locale Selector */}
        <div>
          <Text strong>{t('locale.switchTo')}:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            value={locale}
            onChange={handleLocaleChange}
            options={LOCALE_OPTIONS.map((opt) => ({
              value: opt.value,
              label: `${opt.flag} ${opt.label}`,
            }))}
          />
        </div>

        {/* Credit Limit Demo */}
        <div>
          <Text strong>Credit Limit Value / 信用额度数值:</Text>
          <InputNumber
            style={{ width: '100%', marginTop: 8 }}
            value={value}
            onChange={(val) => setValue(val || 0)}
            min={0}
            step={10000}
          />
        </div>

        {/* Formatting Result */}
        <Card type="inner" title={t('localeDemo.formattedOutput')}>
          <Space direction="vertical" size="small">
            <div>
              <Text type="secondary">Original Value / 原始值: </Text>
              <Text code>{value.toLocaleString()}</Text>
            </div>

            <div>
              <Text type="secondary">Currency / 货币: </Text>
              <Text code>{currency}</Text>
            </div>

            <div>
              <Text type="secondary">Unit / 单位: </Text>
              <Text code>{t('localeDemo.tenThousandUnit')}</Text>
            </div>

            <div style={{ marginTop: 12 }}>
              <Text strong style={{ fontSize: 18 }}>
                Result / 结果:{' '}
              </Text>
              <Text style={{ fontSize: 24, fontWeight: 'bold', color: '#1890ff' }}>
                {formatCreditLimit(value, currency, unit, locale)}
              </Text>
            </div>

            <div style={{ marginTop: 8, padding: 8, backgroundColor: '#f5f5f5', borderRadius: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                💡 {locale === 'zh-CN'
                  ? `${value} = ${(value / 10000).toFixed(2)}万 (万 = 10,000)`
                  : `${value.toLocaleString()} = ${(value / 1000).toFixed(2)}K (K = 1,000)`
                }
              </Text>
            </div>
          </Space>
        </Card>

        {/* Example Comparison */}
        <Card type="inner" title={t('localeDemo.exampleComparison')}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            {[
              10000, 50000, 100000, 150000, 500000, 1000000
            ].map((val) => (
              <div key={val} style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>{val.toLocaleString()}:</Text>
                <Space>
                  <Text>
                    🇨🇳 {formatCreditLimit(val, currency, unit, 'zh-CN')}
                  </Text>
                  <Text type="secondary">|</Text>
                  <Text>
                    🇺🇸 {formatCreditLimit(val, 'USD', unit, 'en-US')}
                  </Text>
                </Space>
              </div>
            ))}
          </Space>
        </Card>

        {/* Implementation Notes */}
        <Card type="inner" title={t('localeDemo.implementationNotes')}>
          <Space direction="vertical" size="small">
            <Text>
              ✅ <Text strong>Extensible:</Text> Easy to add more locales (ja-JP, ko-KR, etc.)
            </Text>
            <Text>
              ✅ <Text strong>Consistent:</Text> Uses same i18n utilities across all components
            </Text>
            <Text>
              ✅ <Text strong>Type-safe:</Text> Full TypeScript support with Locale type
            </Text>
            <Text>
              ✅ <Text strong>Bi-directional:</Text> Both format and parse functions available
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              💡 Future: Can extend to full translation system (UI strings, messages, etc.)
            </Text>
          </Space>
        </Card>
      </Space>
    </Card>
  );
};

/**
 * Simple hook to use locale in components
 * @deprecated Use useLocale from hooks.ts instead
 */
export function useLocale() {
  return useLocaleInternal();
}
